import { Input, Popover, Radio, RadioChangeEvent } from 'antd';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Button } from '../../components/Button/Button';
import { FeedbackRating, IAnalysisReport } from '../../logic/store/Insights/InsightsModels';
import { IConcernFeedbackRequest, sendFeedbackForConcern } from '../../logic/store/Insights/InsightThunks';
import { useAppDispatch } from '../../logic/store/Store';
import { ReactComponent as ThumbsUp } from '../../assets/images/ThumbsUp.svg';
import { ReactComponent as ThumbsDown } from '../../assets/images/ThumbsDown.svg';

const { TextArea } = Input;

const StyledThumbsUp = styled(ThumbsUp)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

const StyledThumbsDown = styled(ThumbsDown)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

interface IConcernFeedbackProps
{
	report: IAnalysisReport;
	concernId?: string; // Optional, if this is attached to just a report no concern Id is needed
}

export const FeedbackThumbs = (props: IConcernFeedbackProps) =>
{
	const theme = useTheme();
	const dispatch = useAppDispatch();

	const { report, concernId } = props;

	// Which thumb’s popover is open: Positive (thumbs up), Negative (thumbs down), or null
	const [activePopover, setActivePopover] = useState<FeedbackRating | null>(null);

	// Comments for this concern (positive or negative)
	const [comments, setComments] = useState<string>('');

	const submitFeedback = (rating: FeedbackRating, commentsText: string) =>
	{
		const trimmed = commentsText.trim();
		if (!trimmed)
		{
			return;
		}

		const request: IConcernFeedbackRequest =
		{
			Persona: report.Persona,
			Subject: report.Subject,
			SnapshotDate: report.SnapshotDate,
			EntityId: report.TargetEntityId,
			ConcernId: concernId,
			Rating: rating,
			Comments: trimmed,
		};

		dispatch(sendFeedbackForConcern(request));

		// Close popover & reset
		setActivePopover(null);
		setComments('');
	};

	const handleSubmit = (e: React.FormEvent, rating: FeedbackRating) =>
	{
		e.preventDefault();
		submitFeedback(rating, comments);
	};

	const renderPopoverContentThumbUp = () => (
		<div onClick={(e) => e.stopPropagation()}>
			<form onSubmit={(e) => handleSubmit(e, FeedbackRating.Positive)}>
				<div style={{ display: 'flex', flexDirection: 'column', rowGap: 10 }}>
					<StyledRadioButton
						style={{ width: '100%' }}
						onClick={(e) =>
						{
							e.preventDefault();      // don't submit the form directly
							e.stopPropagation();     // don't re-toggle popover
							submitFeedback(FeedbackRating.Positive, 'This is great insight');
						}}
					>
						This is great insight
					</StyledRadioButton>
					<StyledRadioButton
						style={{ width: '100%' }}
						onClick={(e) =>
						{
							e.preventDefault();     // don't submit the form directly
							e.stopPropagation();    // don't re-toggle popover
							submitFeedback(FeedbackRating.Positive, 'This is accurate information');
						}}
					>
						This is accurate information
					</StyledRadioButton>
				</div>
				<TextArea
					rows={4}
					style={{ marginTop: 10, resize: 'none' }}
					value={comments}
					onChange={(e) => setComments(e.target.value)}
				/>
				<div style={{ display: 'flex', width: '100%' }}>
					<Button
						variant='main'
						style={{ marginLeft: 'auto', marginTop: 10, width: '50%' }}
						type='submit'
						disabled={!comments.trim()}
					>
						Submit
					</Button>
				</div>
			</form>
		</div>
	);

	const renderPopoverContentThumbDown = () => (
		<div onClick={(e) => e.stopPropagation()}>
			<form onSubmit={(e) => handleSubmit(e, FeedbackRating.Negative)}>
				<div style={{ display: 'flex', flexDirection: 'column', rowGap: 10 }}>
					<StyledRadioButton
						style={{ width: '100%' }}
						onClick={(e) =>
						{
							e.preventDefault();      // don't submit the form directly
							e.stopPropagation();     // don't re-toggle popover
							submitFeedback(FeedbackRating.Negative, 'This is not useful insight');
						}}
					>
						This is not useful insight
					</StyledRadioButton>
					<StyledRadioButton
						style={{ width: '100%' }}
						onClick={(e) =>
						{
							e.preventDefault();     // don't submit the form directly
							e.stopPropagation();    // don't re-toggle popover
							submitFeedback(FeedbackRating.Negative, 'This is incorrect information');
						}}
					>
						This is incorrect information
					</StyledRadioButton>
				</div>
				<TextArea
					rows={4}
					style={{ marginTop: 10, resize: 'none' }}
					value={comments}
					onChange={(e) => setComments(e.target.value)}
				/>
				<div style={{ display: 'flex', width: '100%' }}>
					<Button
						variant='main'
						style={{ marginLeft: 'auto', marginTop: 10, width: '50%' }}
						type='submit'
						disabled={!comments.trim()}
					>
						Submit
					</Button>
				</div>
			</form>
		</div>
	);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				columnGap: 10,
				cursor: 'pointer',
			}}
			onClick={(e) => e.stopPropagation()} // prevent Collapse toggle
		>
			<Popover
				content={renderPopoverContentThumbUp()}
				destroyTooltipOnHide
				placement='bottom'
				trigger='click'
				open={activePopover === FeedbackRating.Positive}
				onOpenChange={(visible) =>
				{
					if (visible)
					{
						setActivePopover(FeedbackRating.Positive);
						setComments('');
					}
					else
					{
						setActivePopover(null);
					}
				}}
			>
				<StyledThumbsUp fillcolor={theme.colors.darkGrey} width={25} height={25} />
			</Popover>

			<Popover
				content={renderPopoverContentThumbDown()}
				destroyTooltipOnHide
				placement='bottom'
				trigger='click'
				open={activePopover === FeedbackRating.Negative}
				onOpenChange={(visible) =>
				{
					if (visible)
					{
						setActivePopover(FeedbackRating.Negative);
						setComments('');
					}
					else
					{
						setActivePopover(null);
					}
				}}
			>
				<StyledThumbsDown fillcolor={theme.colors.darkGrey} width={25} height={25} />
			</Popover>
		</div>
	);
};

const StyledRadioButton = styled(Radio.Button)`
	&& {
    /* Base (unselected) */
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.darkGrey};
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    font-weight: 600;
    width: 100%;
    text-align: center;
    border-radius: 6px !important;

    /* Hover */
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      border-color: ${({ theme }) => theme.colors.primary};
      cursor: pointer;
    }

    /* Selected (core override) */
    &.ant-radio-button-wrapper-checked {
      background-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.white};
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: none;
    }

    /* The ::before pseudo element that uses --antd-wave-shadow-color / background-color */
    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
      background-color: ${({ theme }) => theme.colors.primary};
      box-shadow: none;
    }

	&.ant-radio-button-wrapper:not(:first-child)::before {
	  display: none;
	}

    /* First-child border overrides (kill the blue edges) */
    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):first-child {
      border-color: ${({ theme }) => theme.colors.primary};
    }

    &.ant-radio-button-wrapper-checked:not([class*=' ant-radio-button-wrapper-disabled']).ant-radio-button-wrapper:first-child {
      border-right-color: ${({ theme }) => theme.colors.primary};
    }

    /* Selected + hover */
    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
      background-color: ${({ theme }) => theme.colors.secondary};
      border-color: ${({ theme }) => theme.colors.secondary};
      color: ${({ theme }) => theme.colors.white};
    }

    /* Disabled */
    &.ant-radio-button-wrapper-disabled {
      background-color: ${({ theme }) => theme.colors.lightestGrey};
      color: ${({ theme }) => theme.colors.lightGrey};
      border-color: ${({ theme }) => theme.colors.lightGrey};
      cursor: not-allowed;
    }

    /* Focus */
    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }
  }
`;